import { useCallback, useContext, useState } from 'react'
import { Destination, IGraphArrayPayload } from '../../context/AxiosContext'
import useAppToast from '../../hooks/useAppToast'
import { isOfficeInitialized, makeOfficeErrorToast, outlookDisplayMessageForm } from '../../helpers/outlookHelper'
import { fileDownload } from '../../components/StealthLink'
import useAxios from '../../hooks/useAxios'
import { Message } from '@microsoft/microsoft-graph-types'
import { AxiosInstance } from 'axios'
import AuthorizationContext, { Host } from '../../context/AuthorizationContext'
import { appInsightsReact } from '../../pages/App'
import useUser from '../../hooks/useUser'
import { SeverityLevel } from '@microsoft/applicationinsights-common'

type ReturnProps = {
    setMessage: (message: Message) => void
    loaded: boolean
    isOpening: boolean
    open: () => void
    download: () => void
    openOrDownload: () => void
}

function getMessageId(axios: AxiosInstance, message: Message, internetMessageId?: string): Promise<string> {
    return axios
        .get<IGraphArrayPayload<Message>>('/messages/', {
            baseURL: Destination.Email,
            headers: { SuppressErrors: 'true', Prefer: 'IdType="ImmutableId"' },
            params: {
                $select: 'id,webLink',
                $filter: `internetMessageId eq '${message?.internetMessageId || internetMessageId}'`,
            },
        })
        .then(response => response.data?.value?.[0]?.id)
}

function copyMessage(axios: AxiosInstance, messageId: string, host: Host, currentMailbox: string): Promise<string[]> {
    return axios
        .get<string[]>(
            `/api/copy-message?hostKey=${host.RowKey}&sourceEmail=${encodeURIComponent(host.ReplicationMail)}&messageId=${messageId}&destinationEmail=${encodeURIComponent(currentMailbox)}&destinationFolder=deleteditems`,
            {
                baseURL: process.env.INBOXER_HOST,
            },
        )
        .then(response => response.data)
}

export default function useOpenDownloadMessage(initialMessage?: Message, destination?: Destination): ReturnProps {
    const toast = useAppToast()
    const axios = useAxios()
    const { currentMailbox } = useUser()
    const { host: currentHost } = useContext(AuthorizationContext)

    const [message, setMessage] = useState<Message>(initialMessage)
    const [isOpening, setIsOpening] = useState<boolean>(false)

    const openMessage = useCallback(() => {
        setIsOpening(true)

        //1. Open message using original id (replication mailbox id or current mailbox id)
        outlookDisplayMessageForm(
            message.id,
            () => {
                setIsOpening(false)
            },
            (_asyncResult, error) => {
                console.log(`Couldn't load shared e-mail: `, error.message)
                //2. Open message using current mailbox mailbox id
                displayCurrentMailboxMessage()
            },
        )
    }, [message])

    const displayCurrentMailboxMessage = useCallback(() => {
        //1. fetch message id from current mailbox using message#internetMessageId.
        getMessageId(axios, message)
            .then(id => {
                if (id) {
                    //2. open message using current mailbox message id.
                    outlookDisplayMessageForm(
                        id,
                        () => {
                            setIsOpening(false)
                        },
                        (_asyncResult, error) => {
                            console.log(`Couldn't load current mailbox e-mail: `, error.message)
                            //2.1. copy message to current mailbox and display that message.
                            copyAndDisplayMessage()
                        },
                    )
                } else {
                    console.log(`Couldn't find e-mail in current mailbox`)
                    //3. copy message to current mailbox and display that message.
                    copyAndDisplayMessage()
                }
            })
            .catch(error => {
                console.log(`Couldn't load current mailbox e-mail: `, error.message)
                //4. copy message to current mailbox and display that message.
                copyAndDisplayMessage()
            })
    }, [message])

    const reportOpenError = useCallback(
        (reactComponentPath: string, error?: Error, toastMessage?: string) => {
            console.log(`Couldn't open copied e-mail (${reactComponentPath}): `, error?.message)
            setIsOpening(false)
            makeOfficeErrorToast(toast, undefined, {
                name: '',
                message: toastMessage ?? (error ? `Kon de e-mail niet openen. Outlook meldt fout: ${error.message}` : 'Kon de e-mail niet openen.'),
            })
            appInsightsReact.trackException({
                exception: error ?? { name: '', message: 'Message id is undefined' },
                severityLevel: SeverityLevel.Error,
                properties: {
                    id: message.id,
                    reactComponent: 'useOpenDownloadMessage',
                    reactComponentPath,
                },
            })
        },
        [message],
    )

    const copyAndDisplayMessage = useCallback(() => {
        //1. copy message to current mailbox and display that message.
        //The response contains the immutable REST id and EWS id of the copied message.
        copyMessage(axios, message.id, currentHost, currentMailbox)
            .then(ids => {
                const restId = ids?.[0]
                const ewsId = ids?.[1]

                if (!restId) {
                    reportOpenError('undefinedId', undefined, 'Kon de e-mail niet openen. E-mail niet gevonden in huidige mailbox.')
                    return
                }

                //2. open message using current mailbox rest message id. (new outlook)
                outlookDisplayMessageForm(
                    restId,
                    () => {
                        setIsOpening(false)
                    },
                    () => {
                        //2.1. open message using current mailbox EWS message id. (old outlook)
                        if (!ewsId) {
                            reportOpenError('outlookError')
                            return
                        }
                        outlookDisplayMessageForm(
                            ewsId,
                            () => {
                                setIsOpening(false)
                            },
                            (_asyncResult, error) => reportOpenError('outlookError', error),
                        )
                    },
                )
            })
            .catch(error => reportOpenError('copyMessage', error, 'Kon de e-mail niet openen. Fout bij zoeken naar e-mail.'))
    }, [message, reportOpenError])

    const downloadMessage = useCallback(() => {
        setIsOpening(true)
        //1. download message using replication mailbox message id.
        axios
            .get<Blob>(`/messages/${message.id}/$value`, {
                responseType: 'blob',
                baseURL: destination ?? Destination.ReplicationEmail,
            })
            .then(response => {
                console.log('Serving e-mail as download:', message.id)
                fileDownload(response.data, `${message.id}.eml`)
                setIsOpening(false)
            })
            .catch(error => {
                setIsOpening(false)
                makeOfficeErrorToast(toast, undefined, error as Error)
            })
    }, [message])

    const openOrDownloadMessage = useCallback(() => {
        if (isOfficeInitialized()) {
            openMessage()
        } else {
            downloadMessage()
        }
    }, [message])

    return {
        setMessage,
        loaded: message !== undefined,
        isOpening,
        open: openMessage,
        download: downloadMessage,
        openOrDownload: openOrDownloadMessage,
    }
}
